import { m } from "framer-motion";
import { Layers, Shield, DollarSign, Users, Sparkles, Award } from "lucide-react";

const reasons = [
  {
    icon: Layers,
    title: "Prep Work Done Right",
    desc: "Washing, scraping, sanding, priming, and caulking happen before a drop of finish paint. It's most of the labor — and the entire reason a paint job lasts a decade instead of three years.",
    accent: "primary" as const,
  },
  {
    icon: DollarSign,
    title: "Free, Honest Estimates",
    desc: "We come out, measure, and give you a written price before any work begins. No hidden charges, no surprise add-ons halfway through the job.",
    accent: "secondary" as const,
  },
  {
    icon: Shield,
    title: "Your Home Protected",
    desc: "Furniture moved and covered, floors masked, fixtures and hardware protected. We clean up at the end of every workday, not just the last one.",
    accent: "primary" as const,
  },
  {
    icon: Sparkles,
    title: "Clean, Crisp Lines",
    desc: "Straight cut-in lines, even coverage, and no roller marks or tape bleed. The details are what separate a professional finish from a weekend job.",
    accent: "primary" as const,
  },
  {
    icon: Award,
    title: "Middle Tennessee Local",
    desc: "Based in Murfreesboro and serving 15 cities across Rutherford, Davidson, Williamson, Wilson, Sumner, Maury, Montgomery, and Dickson counties.",
    accent: "secondary" as const,
  },
  {
    icon: Users,
    title: "You Deal With the Owner",
    desc: "Edrick Alamillo runs every job personally. You're not passed between a salesperson, a scheduler, and a crew that's never spoken to you.",
    accent: "primary" as const,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="section-padding bg-accent relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div className="container-max relative z-10">
        <div className="text-center mb-14 md:mb-16">
          <m.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-secondary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Why E &amp; J Painting
          </m.span>
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-accent-foreground mb-4 tracking-tight font-bold"
          >
            Why Choose <span className="text-gradient-primary">Us</span>
          </m.h2>
          <m.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-accent-foreground/70 max-w-2xl mx-auto text-lg"
          >
            Six reasons homeowners and businesses across Middle Tennessee hand us the brush.
          </m.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, i) => (
            <m.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 md:p-8 card-hover-lift"
            >
              <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-5 ${
                reason.accent === "secondary"
                  ? "bg-secondary/25 text-secondary"
                  : "bg-primary/25 text-primary-foreground"
              }`}>
                <reason.icon className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl text-accent-foreground mb-2 tracking-tight font-bold">{reason.title}</h3>
              <p className="text-accent-foreground/70 text-sm leading-relaxed">{reason.desc}</p>
            </m.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
